import {
  BaseLinkData,
  BaseLinkLabelData,
  Mapping,
  MappingLabel,
  ModelConceptHierarchyLabel,
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import {
  LinkData,
  Model,
  NodeData,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isDeletedMapping,
  isDeletedMappingLabel,
  isDeletedModelConceptHierarchy,
  isDeletedModelConceptHierarchyLabel,
  isDeletedModelConceptRelationship,
  isDeletedModelConceptRelationshipLabel,
  isDeletedRelationshipMapping,
  isFileUpload,
  isMapping,
  isMappingLabel,
  isModelConceptClass,
  isModelConceptHierarchy,
  isModelConceptHierarchyLabel,
  isModelConceptRelationship,
  isModelConceptRelationshipLabel,
  isRelationshipMapping,
  isVirtualGraph,
} from 'src/containers/ProjectDiagram/utils/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { createDeletedMapping } from 'src/containers/ProjectDiagram/utils/mapping/createDeletedMapping';
import { createDeletedMappingLabel } from 'src/containers/ProjectDiagram/utils/mapping/createDeletedMappingLabel';
import { createDeletedRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/createDeletedRelationshipMapping';
import { getLabelKeyFromMappingKey } from 'src/containers/ProjectDiagram/utils/mapping/createMappingLabel';
import { createDeletedModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createDeletedModelConceptClass';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { createDeletedModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createDeletedModelConceptHierarchy';
import { createDeletedModelConceptHierarchyLabel } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createDeletedModelConceptHierarchyLabel';
import { getLabelKeyFromHierarchyKey } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchyLabel';
import { createDeletedModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createDeletedModelConceptRelationship';
import { createDeletedModelConceptRelationshipLabel } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createDeletedModelConceptRelationshipLabel';
import { getLabelKeyFromRelationshipKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { createDeletedResource } from 'src/containers/ProjectDiagram/utils/resource/createDeletedResource';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';

export const addDeletedData =
  (dataArray: Array<NodeData | LinkData>) => (model: Model) => {
    if (!model.modelData.draftData) {
      return;
    }

    const updatedNodeKeys: Record<string, string> = {};
    const mappingsByKey: Record<string, Mapping> = {};
    const deletedRelationshipsByIri: Record<
      string,
      ModelConceptRelationship[]
    > = {};
    const addDeletedRelationship = (data: ModelConceptRelationship) => {
      if (!deletedRelationshipsByIri[data.value]) {
        deletedRelationshipsByIri[data.value] = [];
      }
      deletedRelationshipsByIri[data.value].push(data);
    };

    const deletedKeys = new Set<string>();
    const deleteClasses = new Set<string>();
    const deleteResources = new Set<string>();
    const deleteHierarchies = new Set<string>();
    const deleteRelationships = new Set<string>();
    const deleteMappings = new Set<string>();
    const deleteRelationshipMappings = new Set<string>();

    // first pass:
    // - add deleted classes, resources
    // - get initial deleted hierarchies, relationships, mappings, relationship mappings
    dataArray.forEach((data) => {
      if (isModelConceptClass(data)) {
        deletedKeys.add(data.key);
        deleteClasses.add(data.key);

        // add deleted class
        const deleted = createDeletedModelConceptClass(
          data,
          getLabelForIri(model.modelData.labelsByIri, data.value)
        );
        model.addNodeData(deleted);
        updatedNodeKeys[data.key] = deleted.key;
      } else if (isFileUpload(data) || isVirtualGraph(data)) {
        deletedKeys.add(data.key);
        deleteResources.add(data.key);

        // add deleted resource
        const deleted = createDeletedResource(data);
        model.addNodeData(deleted);
        updatedNodeKeys[data.key] = deleted.key;
      } else if (isModelConceptHierarchy(data)) {
        deletedKeys.add(data.key);
        deleteHierarchies.add(data.key);
      } else if (isModelConceptRelationship(data)) {
        deletedKeys.add(data.key);
        deleteRelationships.add(data.key);
        addDeletedRelationship(data);
      } else if (isMapping(data)) {
        deletedKeys.add(data.key);
        deleteMappings.add(data.key);
      } else if (isRelationshipMapping(data)) {
        deletedKeys.add(data.key);
        deleteRelationshipMappings.add(data.key);
      }
    });

    // second pass:
    // - store all mappings for later reference
    // - get additional deleted hierarchies, relationships, mappings
    //   based on previously deleted classes, resources
    model.linkDataArray.forEach((linkData) => {
      // store mappings for later reference
      if (isMapping(linkData)) {
        mappingsByKey[linkData.key] = linkData;
      }

      // add deleted hierarchies (using deleted classes)
      if (
        isModelConceptHierarchy(linkData) &&
        (deletedKeys.has(linkData.from) || deletedKeys.has(linkData.to))
      ) {
        deletedKeys.add(linkData.key);
        deleteHierarchies.add(linkData.key);
      }
      // add deleted relationship (using deleted classes)
      else if (
        isModelConceptRelationship(linkData) &&
        ((linkData.from && deletedKeys.has(linkData.from)) ||
          (linkData.to && deletedKeys.has(linkData.to)))
      ) {
        deletedKeys.add(linkData.key);
        deleteRelationships.add(linkData.key);
        addDeletedRelationship(linkData);
      }
      // add deleted mappings (using deleted classes, resources)
      else if (
        isMapping(linkData) &&
        ((linkData.to && deletedKeys.has(linkData.to)) ||
          deletedKeys.has(linkData.from))
      ) {
        deletedKeys.add(linkData.key);
        deleteMappings.add(linkData.key);
      }
    });

    // third pass:
    // - get additional deleted relationship mappings
    //   based on previously deleted classes, resources, mappings
    model.linkDataArray.forEach((linkData) => {
      if (!isRelationshipMapping(linkData)) {
        return;
      }

      // add deleted relationship mappings (using deleted mappings)
      if (
        (linkData.from && deleteMappings.has(linkData.from)) ||
        (linkData.to && deleteMappings.has(linkData.to))
      ) {
        deletedKeys.add(linkData.key);
        deleteRelationshipMappings.add(linkData.key);
      }

      // add deleted relationship mappings (using deleted relationships)
      const sourceClassMapping = linkData.from
        ? mappingsByKey[linkData.from]
        : undefined;
      if (
        deletedRelationshipsByIri[linkData.relationshipIri]?.find(
          (data) =>
            data.from &&
            (sourceClassMapping?.to === data.from ||
              linkData.fromClassIri === getModelConceptClassValue(data.from))
        )
      ) {
        deletedKeys.add(linkData.key);
        deleteRelationshipMappings.add(linkData.key);
      }

      const targetClassMapping = linkData.to
        ? mappingsByKey[linkData.to]
        : undefined;
      if (
        deletedRelationshipsByIri[linkData.relationshipIri]?.find(
          (data) =>
            data.to &&
            (targetClassMapping?.to === data.to ||
              linkData.toClassIri === getModelConceptClassValue(data.to))
        )
      ) {
        deletedKeys.add(linkData.key);
        deleteRelationshipMappings.add(linkData.key);
      }
    });

    // fourth pass:
    // - add deleted hierarchies, relationships, mappings, relationship mappings
    model.linkDataArray.forEach((linkData) => {
      // add deleted hierarchies
      if (
        isModelConceptHierarchy(linkData) &&
        deleteHierarchies.has(linkData.key)
      ) {
        const deleted = createDeletedModelConceptHierarchy(
          linkData,
          deletedKeys
        );
        model.addLinkData(deleted);

        const labelKey = getLabelKeyFromHierarchyKey(linkData.key);
        const labelData = model.nodeDataArray.find(
          (nodeData): nodeData is ModelConceptHierarchyLabel =>
            isModelConceptHierarchyLabel(nodeData) && nodeData.key === labelKey
        );

        if (labelData) {
          const deletedLabel = createDeletedModelConceptHierarchyLabel(
            labelData,
            deletedKeys
          );
          model.addNodeData(deletedLabel);
          updatedNodeKeys[labelData.key] = deletedLabel.key;
        }
      }
      // add deleted relationships
      else if (
        isModelConceptRelationship(linkData) &&
        deleteRelationships.has(linkData.key)
      ) {
        const deleted = createDeletedModelConceptRelationship(
          linkData,
          getLabelForIri(model.modelData.labelsByIri, linkData.value),
          deletedKeys
        );
        model.addLinkData(deleted);

        const labelKey = getLabelKeyFromRelationshipKey(linkData.key);
        const labelData = model.nodeDataArray.find(
          (nodeData): nodeData is ModelConceptRelationshipLabel =>
            isModelConceptRelationshipLabel(nodeData) &&
            nodeData.key === labelKey
        );

        if (labelData) {
          const deletedLabel = createDeletedModelConceptRelationshipLabel(
            labelData,
            getLabelForIri(model.modelData.labelsByIri, linkData.value),
            deletedKeys
          );
          model.addNodeData(deletedLabel);
          updatedNodeKeys[labelData.key] = deletedLabel.key;
        }
      }
      // add deleted mappings
      else if (isMapping(linkData) && deleteMappings.has(linkData.key)) {
        const deleted = createDeletedMapping(linkData, deletedKeys);
        model.addLinkData(deleted);

        const labelKey = getLabelKeyFromMappingKey(linkData.key);
        const labelData = model.nodeDataArray.find(
          (nodeData): nodeData is MappingLabel =>
            isMappingLabel(nodeData) && nodeData.key === labelKey
        );

        if (labelData) {
          const deletedLabel = createDeletedMappingLabel(
            labelData,
            deletedKeys
          );
          model.addNodeData(deletedLabel);
          updatedNodeKeys[labelData.key] = deletedLabel.key;
        }
      }
      // add deleted relationship mappings
      else if (
        isRelationshipMapping(linkData) &&
        deleteRelationshipMappings.has(linkData.key)
      ) {
        const deleted = createDeletedRelationshipMapping(
          linkData,
          getLabelForIri(model.modelData.labelsByIri, linkData.relationshipIri),
          deletedKeys
        );
        model.addLinkData(deleted);
      }
    });

    // fifth pass:
    // - update existing deleted items
    const replaceKeysForPreviouslyDeletedLink = (
      linkData: BaseLinkData | BaseLinkLabelData
    ) => {
      const oldFrom = linkData.from;
      const newFrom = updatedNodeKeys[oldFrom || ''];
      if (newFrom) {
        setDataProperty<BaseLinkData | BaseLinkLabelData>(model)(
          linkData,
          'from',
          newFrom
        );
      }
      const oldTo = linkData.to;
      const newTo = updatedNodeKeys[oldTo || ''];
      if (newTo) {
        setDataProperty<BaseLinkData | BaseLinkLabelData>(model)(
          linkData,
          'to',
          newTo
        );
      }
    };
    model.nodeDataArray.forEach((nodeData) => {
      if (
        isDeletedModelConceptHierarchyLabel(nodeData) ||
        isDeletedModelConceptRelationshipLabel(nodeData) ||
        isDeletedMappingLabel(nodeData)
      ) {
        replaceKeysForPreviouslyDeletedLink(nodeData);
      }
    });
    model.linkDataArray.forEach((linkData) => {
      if (
        isDeletedModelConceptHierarchy(linkData) ||
        isDeletedModelConceptRelationship(linkData) ||
        isDeletedMapping(linkData) ||
        isDeletedRelationshipMapping(linkData)
      ) {
        replaceKeysForPreviouslyDeletedLink(linkData);
      }
    });
  };
